/**
 * Server Override Utility
 *
 * Allows redirecting MarketData and Router connections to alternate servers
 * for debugging client connection issues.
 *
 * Usage: Add URL parameters to redirect to a different server subdomain:
 *   ?mkd_srv=debug1     → MarketData: https://debug1.tradezone.com.br
 *   ?rtr_srv=hml        → Router: https://hml.tradezone.com.br
 *   ?mkd_srv=test&rtr_srv=test → Both services to test.tradezone.com.br
 *
 * Security: Only allows *.tradezone.com.br subdomains (hardcoded suffix)
 */

const BASE_DOMAIN = "tradezone.com.br";

/**
 * Get server overrides from URL parameters
 * @returns {{ marketDataUrl: string | null, routerUrl: string | null }}
 */
export function getServerOverrides() {
  // Only run in browser environment
  if (typeof window === "undefined" || !window.location) {
    return { marketDataUrl: null, routerUrl: null };
  }

  const params = new URLSearchParams(window.location.search);
  const mkdPrefix = params.get("mkd_srv");
  const rtrPrefix = params.get("rtr_srv");

  const overrides = {
    marketDataUrl: null,
    routerUrl: null,
  };

  if (mkdPrefix && isValidPrefix(mkdPrefix)) {
    overrides.marketDataUrl = `https://${mkdPrefix}.${BASE_DOMAIN}`;
    console.warn(
      `[ServerOverride] MarketData URL override: ${overrides.marketDataUrl}`,
    );
  } else if (mkdPrefix) {
    console.error(
      `[ServerOverride] Invalid mkd_srv prefix rejected: ${mkdPrefix}`,
    );
  }

  if (rtrPrefix && isValidPrefix(rtrPrefix)) {
    overrides.routerUrl = `https://${rtrPrefix}.${BASE_DOMAIN}`;
    console.warn(
      `[ServerOverride] Router URL override: ${overrides.routerUrl}`,
    );
  } else if (rtrPrefix) {
    console.error(
      `[ServerOverride] Invalid rtr_srv prefix rejected: ${rtrPrefix}`,
    );
  }

  return overrides;
}

/**
 * Validate subdomain prefix (basic sanitization)
 * @param {string} prefix - Subdomain prefix to validate
 * @returns {boolean}
 */
function isValidPrefix(prefix) {
  if (!prefix || typeof prefix !== "string") return false;

  // Allow alphanumeric, hyphens, and dots (for multi-level subdomains)
  // Block anything that could be used for injection
  const validPattern = /^[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?$/;

  // Max length check (reasonable subdomain length)
  if (prefix.length > 63) return false;

  // Block consecutive dots or hyphens
  if (/\.\.|-\.|\.--|-{2}/.test(prefix)) return false;

  return validPattern.test(prefix);
}

/**
 * Check if server overrides are active
 * @returns {boolean}
 */
export function hasServerOverrides() {
  const overrides = getServerOverrides();
  return overrides.marketDataUrl !== null || overrides.routerUrl !== null;
}

/**
 * Get display info about active overrides (for UI indicators)
 * @returns {{ active: boolean, mkd: string | null, rtr: string | null }}
 */
export function getOverrideStatus() {
  const overrides = getServerOverrides();
  return {
    active: overrides.marketDataUrl !== null || overrides.routerUrl !== null,
    mkd: overrides.marketDataUrl,
    rtr: overrides.routerUrl,
  };
}
